import type { NewTaskDropAccepts, NewTaskDropTarget } from './newTaskDropTarget'

/** Wohin `handleAddSection` den neuen Abschnitt setzt und welche Aufgaben er aufnimmt. */
export type NewSectionCreation =
  | { anchorId: string; placement: 'before'; packTaskIds: string[] }
  | { position: 'bottom'; packTaskIds: string[] }

export interface SectionCreationForDropTargetInput {
  target: NewTaskDropTarget
  /** Der Abschnitt des Segments, in dem das Ziel liegt — `null` für die Aufgaben ohne Abschnitt ganz oben. */
  segmentSectionId: string | null
  /** ALLE Aufgaben des Projekts, auch ausgeblendete erledigte; Unteraufgaben werden übergangen. */
  tasks: ReadonlyArray<{ id: string; order: number; parentTaskId?: string | null }>
  sections: ReadonlyArray<{ id: string; order: number }>
}

/**
 * **Abschnitt aus der Pille ziehen: Stelle → Anlegen** (#314).
 *
 * Der neue Abschnitt nimmt alles, was unter der Stelle bis zum nächsten Kopf steht, und kommt vor diesen Kopf
 * (sonst ans Ende). Gerechnet wird über `order` ALLER Aufgaben, nicht über die gezeichneten Zeilen: Ausgeblendete
 * erledigte behalten `order` und `sectionId` (ADR 0019) — blieben sie zurück, gehörten sie weiter dem alten
 * Abschnitt, obwohl sie nun unter dem neuen Kopf stehen. Die Reihenfolge ist die der Liste (`ProjectPage`,
 * Segmente): nach `order`, bei Gleichstand die Aufgabe vor dem Kopf.
 *
 * Unteraufgaben folgen ihrer Aufgabe (`reorderTasks` → `moveChildrenWithParent`). Der Kopf eines zugeklappten
 * Abschnitts „ans Ende“ (`end`) ist ein Ziel des Aufgaben-Zugs, nicht dieses — `null`.
 *
 * @example
 * sectionCreationForDropTarget({ target: { kind: 'slot', segmentIndex: 1, afterTaskId: 't3' }, segmentSectionId: 'A', tasks, sections })
 * // → { anchorId: 'B', placement: 'before', packTaskIds: ['t4'] }
 */
export function sectionCreationForDropTarget({
  target, segmentSectionId, tasks, sections,
}: SectionCreationForDropTargetInput): NewSectionCreation | null {
  if (target.kind === 'end') return null

  type Item = { kind: 'task' | 'section'; id: string; order: number }
  const items: Item[] = [
    ...tasks.filter(task => !task.parentTaskId).map(task => ({ kind: 'task' as const, id: task.id, order: task.order })),
    ...sections.map(section => ({ kind: 'section' as const, id: section.id, order: section.order })),
  ]
  // Stabil: bei gleicher `order` bleibt die Aufgabe vor dem Kopf, wie in der Liste.
  items.sort((a, b) => a.order - b.order)

  const indexOf = (id: string) => items.findIndex(item => item.id === id)
  let start: number
  if (target.kind === 'slot' && target.afterTaskId) start = indexOf(target.afterTaskId) + 1
  else if (target.kind === 'after') start = indexOf(target.sectionId) + 1
  else start = segmentSectionId ? indexOf(segmentSectionId) + 1 : 0

  let next = items.findIndex((item, i) => i >= start && item.kind === 'section')
  if (next === -1) next = items.length
  // Hinter einem zugeklappten Kopf gehört alles bis zum nächsten noch ihm.
  const packTaskIds = target.kind === 'after' ? [] : items.slice(start, next).map(item => item.id)

  const anchor = items[next]
  return anchor ? { anchorId: anchor.id, placement: 'before', packTaskIds } : { position: 'bottom', packTaskIds }
}

/**
 * Wo der Abschnitts-Zug landen darf: überall, nur nicht in einem archivierten Abschnitt. Der steht nur in der Liste,
 * weil er noch Offenes trägt — ein neuer Abschnitt darin nähme die beim Archivieren abgehakten Aufgaben mit, und
 * „Mit N Aufgaben wiederherstellen“ fände keine mehr (Code-Review #314).
 */
export function acceptsNewSection(segment: { section?: { archivedAt?: Date | null } | null } | undefined): NewTaskDropAccepts {
  return segment?.section?.archivedAt ? 'none' : 'all'
}
